import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GurujiChatWidget from '../components/GurujiChatWidget';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar, User, Clock, ArrowRight, TrendingUp, 
  FileText, Award, Bell, Search, Loader2
} from 'lucide-react';
import {
  upsertMetaByName,
  upsertMetaByProperty,
  upsertCanonical,
  upsertJsonLd,
  removeJsonLd
} from '../lib/seoHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://vidya365.in';

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Job Notifications', 'Exam Updates', 'Results', 'Admit Cards', 'Study Tips', 'Current Affairs'];

  // Fetch blog posts from backend
  useEffect(() => {
    fetchBlogPosts();
  }, []);

  // SEO — list page meta + Breadcrumb + ItemList schema
  useEffect(() => {
    const title = 'Latest Update — Exam Notifications, Results, Current Affairs | Vidya365';
    const description = 'Stay updated with the latest SSC, IBPS, SBI, RBI Grade B notifications, exam dates, admit cards, results and current affairs — curated by Vidya365.';
    const canonical = `${SITE_URL}/latest-updates`;
    document.title = title;
    upsertMetaByName('description', description);
    upsertMetaByName('keywords', 'SSC notification, IBPS PO notification, SBI PO exam update, RBI Grade B latest update, exam results, admit card, current affairs, Vidya365');
    upsertCanonical(canonical);
    upsertMetaByProperty('og:title', title);
    upsertMetaByProperty('og:description', description);
    upsertMetaByProperty('og:url', canonical);
    upsertMetaByProperty('og:type', 'website');
    upsertMetaByName('twitter:card', 'summary_large_image');
    upsertMetaByName('twitter:title', title);
    upsertMetaByName('twitter:description', description);

    upsertJsonLd('latestupdate-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Latest Update', item: canonical }
      ]
    });

    return () => removeJsonLd('latestupdate-breadcrumb');
  }, []);

  // ItemList schema — refreshes whenever filtered posts change
  useEffect(() => {
    if (!blogPosts.length) return;
    upsertJsonLd('latestupdate-itemlist', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: blogPosts.slice(0, 20).map((p, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `${SITE_URL}/latest-updates/${p.id}`,
        name: p.title
      }))
    });
    return () => removeJsonLd('latestupdate-itemlist');
  }, [blogPosts]);

  const fetchBlogPosts = async () => {
    try {
      console.log('🔍 Fetching blogs from:', `${API_URL}/api/blog`);
      const response = await fetch(`${API_URL}/api/blog`);
      console.log('📡 Response status:', response.status);
      
      const data = await response.json();
      console.log('📦 Blogs received:', data.length, 'posts');
      console.log('📝 Blog titles:', data.map(b => b.title));
      
      setBlogPosts(data);
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching blog posts:', error);
      setLoading(false);
      // Fallback to empty array if API fails
      setBlogPosts([]);
    }
  };

  // Filter posts based on search and category
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPosts = filteredPosts.filter(post => post.featured);
  const allPosts = filteredPosts;

  // Mock trending topics (can be made dynamic later)
  const trendingTopics = [
    'SSC CGL Syllabus 2026',
    'Bank PO Preparation Strategy',
    'RBI Grade B Previous Papers',
    'IBPS PO Exam Pattern',
    'Current Affairs Quiz'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page Header */}
      <section className="bg-gradient-to-b from-red-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Vidya365 Blog
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Latest updates on government jobs, exam notifications, results, and preparation tips
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none text-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Main Blog Content */}
              <div className="lg:col-span-3">
                {/* Category Tabs */}
                <Tabs defaultValue="All" className="mb-8">
                  <TabsList className="flex flex-wrap gap-2 bg-transparent">
                    {categories.map((cat) => (
                      <TabsTrigger 
                        key={cat} 
                        value={cat}
                        className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
                      >
                        {cat}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  <TabsContent value="All">
                    {loading ? (
                      <div className="text-center py-12">
                        <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
                        <p className="text-gray-600 mt-4">Loading posts...</p>
                      </div>
                    ) : allPosts.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <FileText className="mx-auto text-gray-400 mb-4" size={48} />
                        <p className="text-gray-600">No blog posts available yet</p>
                      </div>
                    ) : (
                      <>
                        {/* Featured Posts */}
                        {featuredPosts.length > 0 && (
                          <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                              <TrendingUp className="mr-2 text-red-600" size={24} />
                              Featured Articles
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                              {featuredPosts.map((post) => (
                                <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow border-2 hover:border-red-200">
                                  {post.featured_image && (
                                    <img 
                                      src={post.featured_image} 
                                      alt={post.title}
                                      width="600"
                                      height="192"
                                      loading="lazy"
                                      decoding="async"
                                      className="w-full h-48 object-cover"
                                    />
                                  )}
                                  <CardHeader>
                                    <Badge className="w-fit bg-red-600 text-white mb-2">
                                      {post.category}
                                    </Badge>
                                    <CardTitle className="text-xl hover:text-red-600 transition-colors">
                                      <Link to={`/latest-updates/${post.id}`}>{post.title}</Link>
                                    </CardTitle>
                                    <CardDescription className="text-base mt-2">
                                      {post.excerpt}
                                    </CardDescription>
                                  </CardHeader>
                                  <CardContent>
                                    <div className="flex items-center justify-between text-sm text-gray-500">
                                      <div className="flex items-center space-x-4">
                                        <span className="flex items-center">
                                          <Calendar size={14} className="mr-1" />
                                          {new Date(post.created_at).toLocaleDateString()}
                                        </span>
                                        <span className="flex items-center">
                                          <Clock size={14} className="mr-1" />
                                          {post.read_time}
                                        </span>
                                      </div>
                                    </div>
                                    <Link to={`/latest-updates/${post.id}`}>
                                      <button className="mt-4 text-red-600 font-medium flex items-center hover:underline">
                                        Read More <ArrowRight size={16} className="ml-1" />
                                      </button>
                                    </Link>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* All Posts */}
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900 mb-6">Latest Updates</h2>
                          <div className="space-y-6">
                            {allPosts.map((post) => (
                              <Card key={post.id} className="hover:shadow-lg transition-shadow">
                                <div className="grid md:grid-cols-4 gap-4">
                                  {post.featured_image && (
                                    <img 
                                      src={post.featured_image} 
                                      alt={post.title}
                                      width="300"
                                      height="200"
                                      loading="lazy"
                                      decoding="async"
                                      className="w-full h-full object-cover rounded-l-lg"
                                    />
                                  )}
                                  <div className={post.featured_image ? "md:col-span-3 p-6" : "md:col-span-4 p-6"}>
                                    <Badge className="bg-red-100 text-red-800 mb-2">
                                      {post.category}
                                    </Badge>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-red-600 transition-colors">
                                      <Link to={`/latest-updates/${post.id}`}>{post.title}</Link>
                                    </h3>
                                    <p className="text-gray-600 mb-4">{post.excerpt}</p>
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                                        <span className="flex items-center">
                                          <User size={14} className="mr-1" />
                                          {post.author_name}
                                        </span>
                                        <span className="flex items-center">
                                          <Calendar size={14} className="mr-1" />
                                          {new Date(post.created_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                      <Link to={`/latest-updates/${post.id}`}>
                                        <button className="text-red-600 font-medium flex items-center hover:underline">
                                          Read More <ArrowRight size={16} className="ml-1" />
                                        </button>
                                      </Link>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </TabsContent>
                </Tabs>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                {/* Trending Topics */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <TrendingUp className="mr-2 text-red-600" size={20} />
                      Trending Topics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {trendingTopics.map((topic, index) => (
                        <li key={index}>
                          <Link 
                            to="#" 
                            className="text-gray-700 hover:text-red-600 flex items-center"
                          >
                            <ArrowRight size={14} className="mr-2" />
                            {topic}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Quick Links */}
                <Card className="mb-6 bg-red-50 border-red-200">
                  <CardHeader>
                    <CardTitle className="flex items-center text-red-900">
                      <Bell className="mr-2" size={20} />
                      Get Notifications
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 mb-4">
                      Subscribe to receive instant updates on job notifications and exam alerts
                    </p>
                    <Link to="/contact">
                      <button className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium">
                        Subscribe Now
                      </button>
                    </Link>
                  </CardContent>
                </Card>

                {/* Categories */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="mr-2 text-red-600" size={20} />
                      Categories
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {categories.slice(1).map((cat) => (
                        <li key={cat}>
                          <Link 
                            to="#" 
                            className="text-gray-700 hover:text-red-600 flex items-center justify-between"
                          >
                            <span>{cat}</span>
                            <ArrowRight size={14} />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Floating Guruji AI Chat Widget */}
      <GurujiChatWidget />
    </div>
  );
};

export default Blog;
